import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { MenuItem } from "@mui/material"
import { useState } from "react"

import {
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import JoonSelectInput from "../../components/inputs/JoonSelectInput"
import {
  AbaGeneratedGoal as Goal,
  generateAbaGoals,
} from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "goal_writing_assistant"

const GoalWritingAssistant = requireAuth(() => {
  const [clientInfo, setClientInfo] = useState("")
  const [goalType, setGoalType] = useState("")
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, { goal_type: goalType })
    try {
      const data = await generateAbaGoals(clientInfo, goalType)
      if (!Array.isArray(data.goals)) throw new Error("Invalid response shape")
      setGoals(data.goals)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate goals. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="ABA Goal Writing Assistant"
      description="Pick a goal type and describe the client. We'll draft 5 SMART ABA goals with objectives and measurement plans."
    >
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Goal type
        </Typography>
        <JoonSelectInput
          value={goalType}
          setValue={setGoalType}
          placeholder="Select goal type"
        >
          <MenuItem value="parent_training">Parent training</MenuItem>
          <MenuItem value="learner">Learner</MenuItem>
          <MenuItem value="social_skills">Social skills</MenuItem>
          <MenuItem value="communication">Communication</MenuItem>
          <MenuItem value="behavior_reduction">Behavior reduction</MenuItem>
        </JoonSelectInput>
      </FlexBox>
      <FlexBox direction="column" gap={SPACING.space1}>
        <Typography variant="bodyBold" textAlign="left">
          Client information
        </Typography>
        <TextArea
          name="Client information"
          value={clientInfo}
          onChange={(e) => setClientInfo(e.target.value)}
          placeholder="Context for the goal — client info, current skill levels, areas of need…"
          fullWidth
        />
      </FlexBox>
      <Button
        text="Generate goals"
        onClick={generate}
        disabled={loading || !clientInfo || !goalType}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {goals.length > 0 && (
        <FlexBox direction="column" gap={SPACING.space3}>
          <Typography variant="h3" textAlign="left">
            Generated goals
          </Typography>
          {goals.map((g, i) => (
            <FlexBox
              key={i}
              direction="column"
              gap={SPACING.space1}
              style={{
                border: `1px solid ${JoonUIColor.border.default}`,
                borderRadius: SPACING.space2,
                padding: SPACING.space4,
              }}
            >
              <Typography variant="bodyBold" textAlign="left">
                {g.goalStatement || "Goal statement not available"}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Objective:</strong> {g.objective || "Not available"}
              </Typography>
              <Typography variant="bodySmall" textAlign="left">
                <strong>Measurement:</strong>{" "}
                {g.measurement || "Not available"}
              </Typography>
            </FlexBox>
          ))}
        </FlexBox>
      )}
    </ToolPage>
  )
})

export default GoalWritingAssistant
